// The Clockwork Coliseum promo under the Champ Match cell: rotating "slides" built from
// the game's public read API (https://api.clockworkcoliseum.com, CORS-open). Pure — the
// component fetches, this turns whatever came back into slides, skipping any it can't
// fill. Every slide is { key, kicker, title, sub }.

export const API = 'https://api.clockworkcoliseum.com';

// A wiznaib.us-only redeem code (minted on the game side with its Discord `/promo mint`,
// redeemed in the game's Join tab or the chat's 🎁). Its slide only shows when `code` is
// set; each redemption shows up in the game's activity feed, which is how we can tell
// whether the promo brings anyone in.
// WIZNAIBUS (2026-10-14) grants 750 renown, 500 coin, Choose class + Choose gender, the
// Geomancer/Oracle/Gambler trio and the Geared Up + Dragon's Hoard boon charms.
export const PROMO_CODE = {
  code: 'WIZNAIBUS',
  reward: '750 renown, 500 coin, the new Geomancer, Oracle and Gambler, and two boon charms',
};

// "the tide pools (orig-tide-pools)" → "The Tide Pools"
export function mapName(mapId) {
  const base = String(mapId || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  return base.replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

// "An" before a number read with a vowel sound (8, 11, 18, 80–89, 800…), else "A".
export function aOrAn(n) {
  const t = String(n);
  return t.startsWith('8') || t === '11' || t === '18' ? 'An' : 'A';
}

// 1 → "1st", 2 → "2nd", 13 → "13th"
export function ordinal(n) {
  const t = n % 100, s = t >= 11 && t <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th';
  return n + s;
}

// The champion's place in the season's race to the Tournament of Champions, from the
// game's koth.toc (only sent once a champion has 5+ wins): rank 1 = leading; within the top
// 14, the team above them (nextLabel) and the wins to pass it (toNext); outside it,
// nextLabel is null and toNext is the wins to break into the top 14.
export function tocLine(toc) {
  if (!toc || !toc.rank) return '';
  const w = (n) => (n === 1 ? 'one win' : `${n} wins`);
  if (toc.rank === 1) return 'They lead the race to the Tournament of Champions!';
  if (toc.nextLabel) return `${ordinal(toc.rank)} in the ToC race, ${w(toc.toNext)} from passing ${toc.nextLabel}.`;
  return `${w(toc.toNext)} from the ToC race's top 14.`.replace(/^./, (c) => c.toUpperCase());
}

// 2,365,000 → "2,365,000"
const num = (n) => Number(n).toLocaleString('en-US');

// ms → "3d 4h" / "5h 12m" / "8m"
export function countdown(ms) {
  const m = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
  return d ? `${d}d ${h}h` : h ? `${h}h ${mm}m` : `${mm}m`;
}

// `data` = { state, champion, pulse, season } (any may be missing); `code` = PROMO_CODE.
export function buildSlides(data = {}, { now = Date.now(), code = PROMO_CODE } = {}) {
  const { state, champion, pulse, season } = data;
  const snap = (state && state.snapshot) || {};
  const koth = snap.koth || {};
  const slides = [];

  // 👑 The reigning champion and their streak, against the season record. That's the top
  // peak in the season race (/season), not koth.longestStreak: the server fills that with
  // the best reign STARTED this season, which misses a reign carried over from the last one
  // (Magnar's Mantises' 8 in Season 4, where koth said 6).
  const champLabel = koth.championLabel || (champion && champion.label);
  if (champLabel) {
    const streak = koth.championStreak ?? (champion && champion.streak) ?? 0;
    const record = Math.max(koth.longestStreak || 0, ...((season && season.race) || []).map((r) => r.peak || 0));
    const classes = ((champion && champion.label === champLabel && champion.units) || []).map((u) => u.cls).filter(Boolean);
    const wins = `${streak} win${streak === 1 ? '' : 's'} in a row`;
    const toc = tocLine(koth.toc);
    let sub;
    if (!streak) sub = 'Freshly crowned. Knock them off before they settle in.';
    else if (record && streak >= record) sub = `${wins} — a new season record!`;
    else if (record && streak === record - 1) sub = `${wins}, one from the season record (${record}).`;
    else if (toc) sub = `${wins}. ${toc}`;
    else sub = `${wins}. Can your fighter dethrone them?`;
    slides.push({ key: 'champ', kicker: '👑 Reigning champions', title: champLabel, sub, detail: classes.join(' · ') });
  }

  // ⚔️ The fight on screen right now, and the sim's odds.
  if (snap.teamAName && snap.teamBName) {
    const a = snap.aWinPct, b = snap.bWinPct;
    const where = snap.mapId ? ` on ${mapName(snap.mapId)}` : '';
    let sub = `Live${where}.`;
    if (a != null && b != null) {
      const dog = a < b ? snap.teamAName : snap.teamBName, pct = Math.round(Math.min(a, b) * 100);
      sub = `Live${where}. ${dog} ${pct <= 25 ? 'need a miracle' : 'are the underdog'} at ${pct}%.`;
    }
    slides.push({ key: 'live', kicker: '⚔️ Fighting right now', title: `${snap.teamAName} vs ${snap.teamBName}`, sub });
  }

  // 🎁 The site-only code.
  if (code && code.code) {
    slides.push({
      key: 'code', kicker: '🎁 Only on wiznaib.us', title: `Code ${code.code}`,
      sub: code.reward ? `${code.reward}.` : 'A welcome gift for new fighters.',
      detail: 'Redeem it when you join.',
    });
  }

  // 🏟️ One big number that only grows.
  const battles = pulse && pulse.totals && pulse.totals.battles;
  if (battles) {
    slides.push({ key: 'battles', kicker: '🏟️ The arena never sleeps', title: `${num(battles)} battles fought`, sub: 'AI teams fight around the clock. Queue a fighter any time, even mid-match.' });
  }

  // 🏆 The season: a countdown once the Tournament of Champions is scheduled, else the race.
  // The season's top `raceSize` (14) streaks reach the ToC: the top `qualifySlots` (7) go
  // straight into the tournament proper, the rest through the gauntlet.
  const s = season && season.season;
  if (s) {
    const field = season.raceSize || 14, seeded = season.qualifySlots || 7;
    const toc = s.tocAt ? new Date(s.tocAt).getTime() : NaN;
    const race = (season.race || [])[0];
    if (toc > now) {
      slides.push({
        key: 'toc', kicker: `🏆 Season ${s.id}`, title: `Tournament of Champions in ${countdown(toc - now)}`,
        sub: `The season's top ${seeded} streaks are in, and ${seeded + 1}–${field} fight through the gauntlet.`,
      });
    } else if (race && race.label) {
      const into = season.tournament && s.startTournament ? season.tournament - s.startTournament + 1 : null;
      slides.push({
        key: 'race', kicker: `🏆 Season ${s.id} race leader`, title: race.label,
        sub: `${aOrAn(race.peak)} ${race.peak}-win streak. Any of the season's top ${field} can reach the Tournament of Champions` +
          (into && s.length ? ` (tournament ${num(into)} of ${num(s.length)}).` : '.'),
      });
    }
  }
  return slides;
}

// When the API can't be reached: the pitch on its own, so the promo never goes blank.
export const FALLBACK_SLIDE = {
  key: 'pitch', kicker: '⚔️ A web auto-battler', title: 'A 24/7 arena of AI teams',
  sub: 'Field your own fighter, bet on the fights, and call the shots from the stands.',
};

// The slides to show: the pitch leads whenever there's no live news (the API is down), so
// the promo never shows just the code, or nothing.
export function withFallback(slides) {
  return slides.some((x) => x.key !== 'code') ? slides : [FALLBACK_SLIDE, ...slides];
}
